import React from "react";
import LegalPage from "@/components/LegalPage";

export default function Disclaimer() {
  return (
    <LegalPage
      title="AI Advice Disclaimer & User Acknowledgment"
      lastUpdated="February 14, 2026"
      testid="disclaimer-page"
    >
      <div className="rounded-2xl border border-[#E6D9C4] bg-[#FBF3E4] px-5 py-4">
        <p className="text-sm text-[#5B4A2E] leading-relaxed">
          <strong className="text-[#2D2926]">The short version:</strong>{" "}
          Colleague personas are AI. They can be warm, thoughtful, and often
          helpful — but they can also be wrong, miss context, or reflect the
          quirks of their underlying models. Nothing they say is professional
          advice. You are responsible for decisions you make.
        </p>
      </div>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        1. What Colleague is (and isn&apos;t)
      </h2>
      <p>
        Colleague provides AI-generated conversations with fictional
        &quot;colleague&quot; personas. Everything a persona says is generated
        by a large language model — most often Anthropic&apos;s Claude — based
        on a system prompt and your inputs. The personas are{" "}
        <strong>not real people</strong>. They are <strong>not</strong>{" "}
        licensed HR professionals, therapists, financial advisors, physicians,
        lawyers, or any other regulated professionals. They do not have access
        to your workplace, your company&apos;s policies, or the facts of any
        specific real-world situation beyond what you type into the chat.
      </p>
      <p>
        Everything the personas produce should be treated as{" "}
        <strong>an opinion generated by software</strong>, not as fact, not as
        advice, and not as a recommendation to act.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        2. Not professional advice
      </h2>
      <p>
        Colleague is <strong>not</strong> a substitute for consulting a
        qualified human professional. In particular, you should independently
        verify or seek professional guidance before acting on any Colleague
        conversation that touches on:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Human Resources &amp; Employment decisions</strong> —
          firing, quitting, promotions, disciplinary action, discrimination
          claims, filing complaints, negotiating exits, harassment situations,
          performance reviews, or interpreting employment law or company
          policy.
        </li>
        <li>
          <strong>Financial decisions</strong> — investing, salary
          negotiation, budgeting, tax strategy, retirement planning, taking on
          debt, or any decision with material financial consequences.
        </li>
        <li>
          <strong>Medical, mental-health, or wellness decisions</strong> —
          managing anxiety, depression, burnout, addiction, medication,
          therapy, sleep, or any physical or psychiatric symptom. Colleague is
          <em> not</em> a mental-health service and its personas are{" "}
          <em>not</em> counselors. If you are in crisis, contact your local
          emergency service or a crisis line (in the US, dial 988 for the
          Suicide &amp; Crisis Lifeline).
        </li>
        <li>
          <strong>Legal decisions</strong> — contracts, disputes,
          employment law, immigration, intellectual property, corporate
          governance, or any question that would ordinarily be answered by a
          licensed attorney in your jurisdiction.
        </li>
      </ul>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        3. AI can be wrong
      </h2>
      <p>
        Language models can produce statements that sound confident but are
        factually incorrect, out of date, or invented (&quot;hallucinated&quot;).
        They can also reinforce your existing framing without pushing back
        when they should. You accept that reality when you use Colleague, and
        you agree not to rely on the service as a source of truth.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        4. Your decisions are your own
      </h2>
      <p>
        You are the sole decision-maker for anything you do in your career,
        relationships, health, finances, or life more broadly. Colleague and
        its operators are <strong>not responsible</strong> for the outcome of
        any decision you make, in whole or in part, based on a conversation
        with a persona — even if the persona actively suggested that decision.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        5. Information security
      </h2>
      <p>
        We take reasonable technical and organizational measures to protect
        the data you share with Colleague, including encryption in transit,
        access controls, and reputable infrastructure partners (see our{" "}
        <a
          href="/privacy"
          className="text-[#D4A574] hover:text-[#C99560] underline"
        >
          Privacy Policy
        </a>
        ). No system is perfectly secure, however. You agree not to share
        highly sensitive information you would not be willing to share over
        email — including but not limited to: government identifiers (SSN,
        passport, driver&apos;s license), financial account numbers, payment
        card data outside our Stripe checkout, protected health information,
        legally privileged material, or trade secrets. If you do share such
        information, you do so at your own risk.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        6. Third-party providers
      </h2>
      <p>
        Colleague uses trusted third parties to deliver the service (AI
        inference, payments, email, image generation, hosting). We choose them
        carefully but do not control them. Their availability, accuracy, and
        security are ultimately their responsibility, not ours. See our{" "}
        <a
          href="/privacy"
          className="text-[#D4A574] hover:text-[#C99560] underline"
        >
          Privacy Policy
        </a>{" "}
        for the current list.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        7. Your acknowledgment
      </h2>
      <p>
        By checking the acknowledgment box at signup — or by continuing to use
        Colleague after the effective date of this document — you confirm
        that:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>You have read and understood this Disclaimer.</li>
        <li>
          You understand persona replies are AI-generated opinions, not
          professional advice or facts.
        </li>
        <li>
          You will not rely on Colleague for HR, financial, medical,
          mental-health, or legal decisions without also consulting a
          qualified human professional.
        </li>
        <li>
          You accept sole responsibility for the decisions you make and for
          the sensitivity of the information you choose to share.
        </li>
      </ul>
      <p>
        We record the date and time you accepted this document, together with
        your account identifier, so we have a durable record of your
        acknowledgment.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        8. Limitation of Liability
      </h2>
      <p className="text-[13px] uppercase tracking-wider text-[#9A938A] mt-6">
        The following is written in formal language on purpose.
      </p>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE COLLEAGUE
        SERVICE, ITS OPERATORS, OWNERS, AFFILIATES, LICENSORS, AND SERVICE
        PROVIDERS (COLLECTIVELY, THE &quot;COLLEAGUE PARTIES&quot;) PROVIDE THE
        SERVICE ON AN &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; BASIS
        WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY,
        OR OTHERWISE, INCLUDING WITHOUT LIMITATION IMPLIED WARRANTIES OF
        MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY,
        NON-INFRINGEMENT, AND ANY WARRANTY ARISING FROM COURSE OF DEALING OR
        USAGE OF TRADE.
      </p>
      <p>
        IN NO EVENT SHALL THE COLLEAGUE PARTIES BE LIABLE FOR ANY INDIRECT,
        INCIDENTAL, CONSEQUENTIAL, SPECIAL, EXEMPLARY, OR PUNITIVE DAMAGES;
        LOST PROFITS OR REVENUES; LOSS OF DATA; LOSS OF EMPLOYMENT; LOSS OF
        BUSINESS OPPORTUNITY; PERSONAL, EMOTIONAL, OR REPUTATIONAL HARM; OR
        ANY DAMAGES ARISING OUT OF OR RELATED TO A USER&apos;S RELIANCE ON,
        USE OF, OR INABILITY TO USE THE SERVICE OR ANY PERSONA OUTPUT,
        REGARDLESS OF THE LEGAL THEORY (CONTRACT, TORT, STATUTE, OR
        OTHERWISE) AND REGARDLESS OF WHETHER THE COLLEAGUE PARTIES WERE
        ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
      </p>
      <p>
        WITHOUT LIMITING THE FOREGOING, THE COLLEAGUE PARTIES SHALL NOT BE
        LIABLE FOR ANY OUTCOME — FINANCIAL, PROFESSIONAL, MEDICAL, LEGAL,
        RELATIONAL, OR OTHERWISE — RESULTING FROM A USER&apos;S DECISION TO
        ACT, OR NOT ACT, IN RELIANCE ON A PERSONA CONVERSATION.
      </p>
      <p>
        THE TOTAL AGGREGATE LIABILITY OF THE COLLEAGUE PARTIES ARISING OUT OF
        OR RELATED TO THIS DISCLAIMER OR YOUR USE OF THE SERVICE SHALL NOT
        EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO COLLEAGUE IN THE
        TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM, OR
        (B) FIFTY UNITED STATES DOLLARS (US$50).
      </p>
      <p>
        SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF
        CERTAIN DAMAGES. IN SUCH JURISDICTIONS, THE COLLEAGUE PARTIES&apos;
        LIABILITY IS LIMITED TO THE MAXIMUM EXTENT PERMITTED BY LAW.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        9. Indemnification
      </h2>
      <p>
        You agree to defend, indemnify, and hold harmless the Colleague
        Parties from and against any and all claims, damages, obligations,
        losses, liabilities, costs, and expenses (including reasonable
        attorneys&apos; fees) arising from (a) any action you take, or fail to
        take, in reliance on a persona conversation, and (b) your breach of
        this Disclaimer or the{" "}
        <a
          href="/terms"
          className="text-[#D4A574] hover:text-[#C99560] underline"
        >
          Terms of Service
        </a>
        .
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        10. Governing law &amp; changes
      </h2>
      <p>
        This Disclaimer is governed by the same law and change-notification
        rules as our{" "}
        <a
          href="/terms"
          className="text-[#D4A574] hover:text-[#C99560] underline"
        >
          Terms of Service
        </a>
        . If we materially update this document, we will notify you in-app and
        by email at least 14 days before the change takes effect, and ask you
        to re-acknowledge it.
      </p>

      <h2 className="font-serif text-2xl text-[#2D2926] mt-10">
        11. Contact
      </h2>
      <p>
        Questions about this Disclaimer? Email us at{" "}
        <a
          href="mailto:hello@meetacolleague.com"
          className="text-[#D4A574] hover:text-[#C99560] underline"
        >
          hello@meetacolleague.com
        </a>
        .
      </p>

      <p className="mt-12 text-xs text-[#9A938A] italic">
        This document is written in plain English for clarity. It is not a
        substitute for advice from a licensed attorney in your jurisdiction.
        You should have counsel review it before you rely on it in any dispute.
      </p>
    </LegalPage>
  );
}
